<template>
  <div
    class="bg-top-navigation fixed top-0 right-0 left-0 z-20 flex h-16 items-center justify-between space-x-4 border-b px-4 lg:px-8"
  >
    <div class="flex min-w-0 items-center gap-1.5 md:gap-4">
      <SidebarTrigger class="md:hidden" />
      <RouterLink
        :to="{ name: 'project', params: { project_uuid: 'default' } }"
        class="hidden items-center md:flex"
      >
        <LogoGradient class="h-7 w-7" />
      </RouterLink>
      <div class="text-muted-foreground/30 hidden text-2xl sm:block">/</div>
      <div class="flex min-w-0 items-center gap-2">
        <RouterLink
          class="min-w-0 font-medium"
          :to="team ? { name: 'team', params: { uuid: team.uuid } } : { name: 'profile' }"
        >
          <div v-if="team" class="flex items-center gap-1.5 truncate">
            <span class="max-w-32 truncate">{{ team.name }}</span>
            <Badge variant="outline">Team</Badge>
          </div>
          <div v-else class="flex items-center gap-1.5">
            <span class="min-w-10 truncate"
              >{{ profile?.first_name }} {{ profile?.last_name }}</span
            >
          </div>
        </RouterLink>
        <Popover v-model:open="teamMenuOpen">
          <PopoverTrigger as-child>
            <Button
              variant="ghost"
              role="combobox"
              :aria-expanded="teamMenuOpen"
              class="px-1"
              size="sm"
            >
              <ChevronsUpDownIcon class="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" class="w-[280px] p-0">
            <Command highlight-on-hover>
              <CommandInput class="h-9" placeholder="Search team..." />
              <CommandList>
                <CommandEmpty>No teams found.</CommandEmpty>
                <div
                  v-if="teams && teams.teams.length === 0"
                  class="text-muted-foreground flex flex-col items-center gap-2 px-4 py-6 text-center text-sm"
                >
                  <UsersIcon class="h-5 w-5" />
                  <div class="flex flex-col gap-1">
                    <p class="text-foreground font-medium">No teams yet</p>
                    <p class="text-xs">Create a team to collaborate with others.</p>
                  </div>
                </div>
                <CommandGroup v-if="teams && teams.teams.length > 0" heading="Teams">
                  <CommandItem
                    v-for="item in teams?.teams"
                    :key="item.uuid"
                    :value="item.uuid"
                    class="group flex items-center justify-between gap-4"
                    @select="
                      (ev) => {
                        // if (typeof ev.detail.value === 'string') {
                        //   // value = ev.detail.value;
                        // }
                        handleTeamSelect(item);
                        teamMenuOpen = false;
                      }
                    "
                  >
                    <div class="flex min-w-0 items-center gap-2">
                      <div
                        class="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs"
                        :style="{ backgroundColor: item.color }"
                        :class="[isColorLight(item.color) ? 'text-black' : 'text-white']"
                      >
                        {{ item.name[0]?.toUpperCase() }}
                      </div>
                      <div class="flex min-w-0 items-center gap-2">
                        <div class="truncate font-normal">{{ item.name }}</div>
                        <Badge
                          v-if="isBillingEnabled && !item.is_billing_ready"
                          variant="outline"
                          class="shrink-0 border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-600 dark:text-amber-400"
                        >
                          Action required
                        </Badge>
                      </div>
                    </div>
                    <div class="flex items-center gap-1.5" v-if="currentTeamUuid === item.uuid">
                      <Check
                        :class="
                          cn(
                            'ml-auto h-4 w-4',
                            currentTeamUuid === item.uuid ? 'opacity-100' : 'opacity-0',
                          )
                        "
                      />
                      <RouterLink
                        :to="{ name: 'team', params: { uuid: item.uuid } }"
                        :class="
                          cn(
                            buttonVariants({ size: 'icon', variant: 'outline' }),
                            'hidden h-5 w-5 group-hover:inline-flex',
                          )
                        "
                      >
                        <Settings2Icon class="size-3" />
                      </RouterLink>
                    </div>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator v-if="team" />
                <CommandGroup v-if="team" heading="Personal account">
                  <CommandItem
                    class="flex items-center justify-between"
                    :value="{ name: `${profile?.first_name} ${profile?.last_name}` }"
                    @select.prevent="
                      () => {
                        handlePersonalAccountSelect();
                        teamMenuOpen = false;
                      }
                    "
                  >
                    <div class="flex items-center gap-0.5">
                      <img
                        :src="profile?.gravatar_url"
                        alt="Profile picture"
                        class="mr-2 h-6 w-6 shrink-0 rounded bg-gray-300"
                      />
                      {{ profile?.first_name }} {{ profile?.last_name }}
                    </div>
                    <Check :class="cn('ml-auto h-4 w-4', !team ? 'opacity-100' : 'opacity-0')" />
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </CommandList>
            </Command>
            <div class="p-1">
              <RouterLink
                class="hover:bg-muted flex items-center rounded px-2 py-1"
                :to="{ name: 'team/new' }"
                @click="teamMenuOpen = false"
              >
                <div class="bg-muted mr-2 flex h-7 w-7 items-center justify-center rounded border">
                  <UsersIcon class="h-4 w-4" />
                </div>
                <span>Create team</span>
              </RouterLink>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div class="text-muted-foreground/30 text-2xl">/</div>
      <div class="flex min-w-0 items-center gap-2">
        <RouterLink
          class="min-w-0 font-medium"
          :to="
            currentProject
              ? { name: 'project', params: { project_uuid: currentProject.uuid } }
              : { name: 'project', params: { project_uuid: 'default' } }
          "
        >
          <div class="min-w-10 truncate">
            {{ currentProject?.name }}
          </div>
        </RouterLink>
        <Popover v-model:open="projectMenuOpen">
          <PopoverTrigger as-child>
            <Button
              variant="ghost"
              role="combobox"
              :aria-expanded="projectMenuOpen"
              class="px-1"
              size="sm"
            >
              <ChevronsUpDownIcon class="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" class="w-[200px] p-0">
            <Command highlight-on-hover>
              <CommandInput class="h-9" placeholder="Search project..." />
              <CommandEmpty>No projects found.</CommandEmpty>
              <CommandList>
                <CommandGroup heading="Projects">
                  <CommandItem
                    v-for="item in projects"
                    :key="item.uuid"
                    :value="item.uuid"
                    class="flex items-center justify-between"
                    as-child
                  >
                    <RouterLink
                      :to="{ name: 'project', params: { project_uuid: item.uuid } }"
                      @click="projectMenuOpen = false"
                    >
                      <div class="flex min-w-0 items-center gap-2">
                        <div
                          class="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs"
                          :style="{ backgroundColor: item.color }"
                          :class="[isColorLight(item.color) ? 'text-black' : 'text-white']"
                        >
                          {{ item.name[0]?.toUpperCase() }}
                        </div>
                        <div class="min-w-0">
                          <div class="truncate font-normal">{{ item.name }}</div>
                          <!-- <p class="text-muted-foreground text-xs">{{ item.members.length }} members</p> -->
                        </div>
                      </div>
                      <Check
                        :class="
                          cn(
                            'ml-auto h-4 w-4',
                            projectUuid === item.uuid ? 'opacity-100' : 'opacity-0',
                          )
                        "
                      />
                    </RouterLink>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </CommandList>
            </Command>
            <div class="p-1">
              <RouterLink
                class="hover:bg-muted flex items-center rounded px-2 py-1"
                :to="{ name: 'project/create' }"
                @click="projectMenuOpen = false"
              >
                <div class="bg-muted mr-2 flex h-7 w-7 items-center justify-center rounded border">
                  <FolderPlusIcon class="h-4 w-4" />
                </div>
                <span>Create project</span>
              </RouterLink>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
    <div class="flex shrink-0 items-center space-x-2 md:space-x-4">
      <DeployMenu />

      <Tooltip>
        <TooltipTrigger>
          <Button variant="ghost" size="icon" @click="notificationsSheetOpen = true">
            <div class="relative">
              <InboxIcon :size="20" />
              <Status
                v-if="notificationsCounter && notificationsCounter > 0"
                class="-top-1"
                status="inactive"
                size="sm"
              />
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Notifications</TooltipContent>
      </Tooltip>

      <TopUserMenu />

      <NotificationsSheet v-model:open="notificationsSheetOpen" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Check,
  ChevronsUpDownIcon,
  FolderPlusIcon,
  InboxIcon,
  Settings2Icon,
  UsersIcon,
} from '@lucide/vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useNotifications } from '@/entities/notification';
import { profileQueryOptions } from '@/entities/profile';
import { projectsQueryOptions } from '@/entities/project';
import { type Team, teamQueryOptions, teamsQueryOptions } from '@/entities/team';
import { IS_BILLING_ENABLED } from '@/shared/config';
import { useGlobalLoader } from '@/shared/hooks';
import { useTeamCookie } from '@/shared/hooks/use-team-cookie';
import { useTeamSwitch } from '@/shared/hooks/use-team-switch';
import LogoGradient from '@/shared/icons/LogoGradient.vue';
import { cn, isColorLight } from '@/shared/lib/misc';
import { Badge } from '@/shared/ui/badge';
import { Button, buttonVariants } from '@/shared/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/shared/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { SidebarTrigger } from '@/shared/ui/sidebar';
import Status from '@/shared/ui/status.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import DeployMenu from '@/widgets/deploy-menu.vue';
import TopUserMenu from '@/widgets/top-user-menu.vue';

import NotificationsSheet from './notifications-sheet.vue';

const route = useRoute();
const router = useRouter();
const { data: projects } = useQuery(projectsQueryOptions());
const { data: profile } = useQuery(profileQueryOptions());
const { data: notifications } = useNotifications();
const teamMenuOpen = ref(false);
const projectMenuOpen = ref(false);
const notificationsSheetOpen = ref(false);
const { data: teams } = useQuery(teamsQueryOptions());
const { currentTeamUuid } = useTeamCookie();
const { data: team } = useQuery(teamQueryOptions(currentTeamUuid));
const { toggle: toggleGlobalLoader } = useGlobalLoader();
const { switchToPersonalAccount, switchToTeam } = useTeamSwitch();
const isBillingEnabled = IS_BILLING_ENABLED;

const projectUuid = computed(() => route.params.project_uuid as string | undefined);

const currentProject = computed(() => {
  return (
    projects.value?.find((item) => item.uuid === projectUuid.value) ??
    projects.value?.find((item) => item.is_default)
  );
});

const notificationsCounter = computed(
  () => notifications.value?.filter((n) => !n.viewed_by_user).length,
);

async function handleTeamSelect(nextTeam: Team) {
  if (nextTeam.uuid === team.value?.uuid) return;

  toggleGlobalLoader(`Switching to ${nextTeam.name} team...`);

  await switchToTeam(nextTeam);

  if (IS_BILLING_ENABLED && !nextTeam.is_billing_ready) {
    const isOwner = nextTeam.members.some(
      (m) => m.uuid === profile.value?.uuid && m.role === 'Owner',
    );
    await router.push({ name: isOwner ? 'team/billing' : 'access-restricted' });
  } else {
    await router.push({ name: 'project', params: { project_uuid: 'default' } });
  }

  toggleGlobalLoader();
}

async function handlePersonalAccountSelect() {
  toggleGlobalLoader(`Switching to personal account...`);

  await switchToPersonalAccount();

  await router.push({ name: 'project', params: { project_uuid: 'default' } });

  toggleGlobalLoader();
}
</script>
